import { getIsLocalDev } from '../helpers';
import { AnalyticsEventCategory, GoogleAnalytics } from './googleAnalytics';

export enum AnalyticsEvent {
  load = 'load',
  loadInlineCarousel = 'load_inline_carousel',
  login = 'login',
  signUp = 'sign_up',
  skipSignIn = 'skip_sign_in',
  open = 'open',
  close = 'close',
  bodyInfoBegin = 'body_info_begin',
  bodyInfoEnd = 'body_info_end',
  pageView = 'page_view',
  submitPostBegin = 'submit_post_begin',
  submitPostEnd = 'submit_post_end',
  submitPostSuccess = 'submit_post_success',
  submitPostError = 'submit_post_error',
  submittedAllItems = 'submit_all_items',
  showMatches = 'show_body_doubles',
  loadHasPosts = 'load_has_posts',
  loatHasPostsMultiProducts = 'load_has_posts_multi_products',
  noMatches = 'no_body_doubles',
  sizeRecommendation = 'size_recommendation',
  purchase = 'purchase',
  authTimeoutRefetch = 'auth_timeout_refetch',
  didFetchAuth = 'did_fetch_auth',
  openHelp = 'open_help',
  goToPost = 'go_to_post',
  addToCart = 'add_to_cart',
  reset = 'reset',
}

export interface AnalyticsDimensions {
  provider: string | undefined;
  [x: string]: string | undefined | number | boolean | unknown;
}

export interface OpenHelpDimensions extends AnalyticsDimensions {
  type: string;
}

export interface ScreenViewDimensions extends AnalyticsDimensions {
  page_title: string;
}

export interface ExceptionDimensions extends AnalyticsDimensions {
  fatal?: boolean;
  description?: string;
}

export interface PostEventDimensions extends AnalyticsDimensions {
  item_id: string | undefined;
  name: string | undefined;
  brand: string | undefined;
  size: string | undefined;
}

export interface WidgetEventDimensions extends AnalyticsDimensions {
  item_id: string;
  name: string;
  brand: string;
  entry_id?: string;
  post_id?: string;
  post_media_type?: string;
}

export interface SalesEventItem {
  item_id: string;
  item_name?: string;
  item_brand?: string;
  item_variant?: string;
}

export interface AddToCartDimensions extends WidgetEventDimensions {
  items: SalesEventItem[];
}

export interface PurchaseDimensions extends AnalyticsDimensions {
  transaction_id: string;
  items: SalesEventItem[];
}

export interface PostInteractionEventDimensions extends WidgetEventDimensions {
  index: number;
  last_post_index: number;
}

export interface MatchEventDimensions extends WidgetEventDimensions {
  count: number;
}

export interface WidgetEntryDimensions extends AnalyticsDimensions {
  entry_id: string;
}

export interface AnalyticsProps {
  category: AnalyticsEventCategory;
  event: AnalyticsEvent | string;
  name?: string;
  dimensions?: AnalyticsDimensions;
}

export const AnalyticsReportingDisabledKey = 'vesticoReportingDisabled';
let analyticsReportingEnabled: boolean | undefined;
let gaInstance: GoogleAnalytics | undefined;

const getAnalyticsReportingEnabled = () => {
  if (analyticsReportingEnabled === undefined) {
    analyticsReportingEnabled = window && typeof window !== 'undefined' && !(window as any)[AnalyticsReportingDisabledKey];
  }
  return analyticsReportingEnabled;
};

const getGoogleAnalyticInstance = () => {
  if (!gaInstance && typeof window !== 'undefined' && getAnalyticsReportingEnabled()) {
    const gtmID = process.env.NEXT_PUBLIC_GTM_ID;
    if (!gtmID) {
      throw new Error('GTM ID mising as env');
    }
    gaInstance = new GoogleAnalytics({ gtmID, debug: getIsLocalDev() });
  }
  return gaInstance;
};

export const logAnalyticsEvent = ({ category, event, name, dimensions }: AnalyticsProps) => {
  const gaInstance = getGoogleAnalyticInstance();
  if (!gaInstance) {
    return;
  }
  try {
    gaInstance?.trackEvent(category, event.toString(), name, undefined, dimensions);
  } catch (error) {
    console.error(error);
  }
};

export const trackRoute = (url: string) => {
  const gaInstance = getGoogleAnalyticInstance();

  try {
    gaInstance?.trackRoute(url);
  } catch (error) {
    console.error(error);
  }
};
